import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, School, ArrowLeft, GraduationCap, Users, BookOpen, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_IMAGE = 'https://images.pexels.com/photos/5212329/pexels-photo-5212329.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750';

const ROLES = [
  { key: 'staff', label: 'Staff / Admin', desc: 'Teachers, HR, Admins', icon: Users, color: 'teal' },
  { key: 'parent', label: 'Parent', desc: 'View child\'s progress', icon: GraduationCap, color: 'violet' },
  { key: 'student', label: 'Student', desc: 'View results & fees', icon: BookOpen, color: 'cyan' },
];

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1); // 1=school code, 2=role select, 3=credentials
  const [schoolCode, setSchoolCode] = useState(searchParams.get('code') || '');
  const [schoolData, setSchoolData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  // Staff fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Parent fields
  const [parentMode, setParentMode] = useState('login'); // 'login' or 'register'
  const [parentEmail, setParentEmail] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [showParentPassword, setShowParentPassword] = useState(false);
  const [parentFullName, setParentFullName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentRelation, setParentRelation] = useState('father');
  const [parentAdmission, setParentAdmission] = useState('');
  // Student fields
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [dob, setDob] = useState('');

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('code')) handleVerifyCode(searchParams.get('code'));
  }, []);

  const handleVerifyCode = async (code) => {
    const cleaned = (code || schoolCode).trim().toUpperCase();
    if (!cleaned || cleaned.length < 3) { setCodeError('Please enter a valid school code'); return; }
    setVerifying(true);
    setCodeError('');
    try {
      const res = await axios.get(`${API_URL}/auth/verify-school-code/${cleaned}`);
      const data = res.data.data;
      setSchoolData(data);
      setSchoolCode(cleaned);
      if (data.branding?.logo_path) setLogoUrl(`${API_URL}/files/${data.branding.logo_path}`);
      setStep(2);
    } catch {
      setCodeError('School code not found. Check with your school admin.');
    } finally {
      setVerifying(false);
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setStep(3);
  };

  const handleBack = () => {
    if (step === 3) { setStep(2); setSelectedRole(''); }
    else if (step === 2) { setStep(1); setSchoolData(null); setLogoUrl(null); setCodeError(''); }
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, schoolCode);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleParentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/parent/login`, { email: parentEmail, password: parentPassword, school_code: schoolCode });
      const data = res.data.data;
      localStorage.setItem('parent_token', data.access_token);
      localStorage.setItem('parent_user', JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.full_name}!`);
      navigate('/parent/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleParentRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/parent/register`, {
        email: parentEmail, password: parentPassword,
        full_name: parentFullName, phone: parentPhone,
        relation: parentRelation, student_admission_number: parentAdmission,
        school_code: schoolCode,
      });
      const data = res.data.data;
      localStorage.setItem('parent_token', data.access_token);
      localStorage.setItem('parent_user', JSON.stringify({ full_name: parentFullName, email: parentEmail, role: 'parent' }));
      toast.success(`Registered! Linked to ${data.student.full_name}`);
      navigate('/parent/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/student/login`, { admission_number: admissionNumber.trim(), date_of_birth: dob, school_code: schoolCode });
      const data = res.data.data;
      localStorage.setItem('student_token', data.access_token);
      localStorage.setItem('student_user', JSON.stringify(data.user));
      toast.success(`Welcome, ${data.user.full_name}!`);
      navigate('/student/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const primaryColor = schoolData?.branding?.primary_color || '#14b8a6';
  const bgColor = schoolData?.branding?.secondary_color || '#0c1222';
  const brandName = schoolData?.branding?.school_name || schoolData?.school_name || '';
  const tagline = schoolData?.branding?.tagline || '';

  const roleInfo = ROLES.find(r => r.key === selectedRole);

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Image Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" data-testid="login-image-panel">
        <img src={DEFAULT_IMAGE} alt="School classroom" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16 w-full">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="h-10 w-10 rounded-xl bg-teal-500 flex items-center justify-center shadow-lg">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-white tracking-tight">Ariskool</span>
          </div>
          <h2 className="text-3xl font-bold text-white leading-snug mb-3">
            Complete School ERP<br />for Modern Institutions
          </h2>
          <p className="text-white/60 text-sm max-w-md leading-relaxed">
            Manage students, staff, fees, exams, attendance, and more — all from one unified platform built for K-12 schools.
          </p>
        </div>
      </div>

      {/* Right Login Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10 relative overflow-hidden transition-colors duration-500" style={{ background: bgColor }} data-testid="login-form-panel">
        <div className="absolute top-[-200px] right-[-100px] w-[400px] h-[400px] rounded-full blur-[120px] pointer-events-none" style={{ background: `${primaryColor}12` }} />
        <div className="absolute bottom-[-100px] left-[-50px] w-[300px] h-[300px] rounded-full blur-[100px] pointer-events-none" style={{ background: `${primaryColor}08` }} />

        <div className="w-full max-w-md relative z-10">

          {/* ── STEP 1: School Code ── */}
          {step === 1 && (
            <div data-testid="login-step-1">
              <div className="text-center mb-8 lg:hidden">
                <Link to="/" className="inline-flex items-center gap-2.5 mb-4">
                  <div className="h-10 w-10 rounded-xl bg-teal-500 flex items-center justify-center shadow-lg shadow-teal-500/20">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-white tracking-tight">Ariskool</span>
                </Link>
              </div>
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-white mb-2" data-testid="login-title">Welcome to Ariskool</h1>
                <p className="text-sm text-white/50">Enter your school code to get started</p>
              </div>
              <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 backdrop-blur-xl">
                <form onSubmit={(e) => { e.preventDefault(); handleVerifyCode(); }} className="space-y-5" data-testid="school-code-form">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-xs font-medium">School Code</Label>
                    <div className="relative">
                      <School className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                      <Input type="text" placeholder="e.g. DPS-2847" value={schoolCode}
                        onChange={(e) => { setSchoolCode(e.target.value.toUpperCase()); setCodeError(''); }}
                        autoFocus
                        className="h-12 pl-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 focus:border-teal-500/50 focus:ring-teal-500/10 uppercase tracking-[0.15em] font-mono text-center text-lg"
                        data-testid="login-school-code-input" />
                    </div>
                    {codeError && <p className="text-xs text-red-400/80 mt-1" data-testid="school-code-error">{codeError}</p>}
                  </div>
                  <Button type="submit" className="w-full h-11 font-semibold rounded-xl shadow-lg text-white bg-teal-500 hover:bg-teal-400" disabled={verifying || !schoolCode.trim()} data-testid="verify-school-code-btn">
                    {verifying ? <span className="flex items-center gap-2"><span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Verifying...</span> : <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </form>
                <p className="text-xs text-white/20 text-center mt-4">Ask your school administrator for the code</p>
              </div>
              {/* <p className="mt-6 text-center text-sm text-white/40">
                Don't have an account? <Link to="/register" className="font-medium text-teal-400 hover:text-teal-300" data-testid="login-register-link">Sign up</Link>
              </p> */}
            </div>
          )}

          {/* ── STEP 2: Role Selection ── */}
          {step === 2 && schoolData && (
            <div data-testid="login-step-2">
              <button onClick={handleBack} className="flex items-center gap-1 text-sm text-white/40 hover:text-white/60 transition-colors mb-6" data-testid="back-to-school-code-btn">
                <ArrowLeft className="h-4 w-4" /> Change school
              </button>
              <div className="text-center mb-8" data-testid="school-branding-display">
                {logoUrl ? (
                  <img src={logoUrl} alt="School Logo" className="h-14 w-14 object-contain mx-auto mb-3 rounded-xl shadow-lg" data-testid="school-logo" onError={(e) => { e.target.style.display = 'none'; setLogoUrl(null); }} />
                ) : (
                  <div className="h-14 w-14 rounded-xl mx-auto mb-3 flex items-center justify-center shadow-lg" style={{ background: primaryColor }}>
                    <span className="text-white text-xl font-bold">{brandName.charAt(0)}</span>
                  </div>
                )}
                <h2 className="text-lg font-bold text-white" data-testid="school-name-display">{brandName}</h2>
                {tagline && <p className="text-xs text-white/40 mt-1">{tagline}</p>}
                <p className="text-xs text-white/25 font-mono mt-1 tracking-wider">{schoolCode}</p>
              </div>

              <p className="text-sm text-white/50 mb-4 text-center">I am logging in as...</p>
              <div className="space-y-3" data-testid="role-selection">
                {ROLES.map(role => {
                  const Icon = role.icon;
                  const colors = { teal: 'border-teal-500/30 bg-teal-500/5 hover:bg-teal-500/10', violet: 'border-violet-500/30 bg-violet-500/5 hover:bg-violet-500/10', cyan: 'border-cyan-500/30 bg-cyan-500/5 hover:bg-cyan-500/10' };
                  const iconColors = { teal: 'text-teal-400 bg-teal-500/20', violet: 'text-violet-400 bg-violet-500/20', cyan: 'text-cyan-400 bg-cyan-500/20' };
                  return (
                    <button key={role.key} onClick={() => handleRoleSelect(role.key)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${colors[role.color]}`}
                      data-testid={`role-${role.key}-btn`}>
                      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${iconColors[role.color]}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="text-white font-semibold text-sm">{role.label}</p>
                        <p className="text-white/40 text-xs">{role.desc}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-white/20" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STEP 3: Credentials ── */}
          {step === 3 && schoolData && (
            <div data-testid="login-step-3">
              <button onClick={handleBack} className="flex items-center gap-1 text-sm text-white/40 hover:text-white/60 transition-colors mb-6" data-testid="back-to-roles-btn">
                <ArrowLeft className="h-4 w-4" /> Change role
              </button>

              {/* School + Role Badge */}
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: primaryColor }}>
                  <span className="text-white text-sm font-bold">{brandName.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{brandName}</p>
                  <p className="text-white/40 text-xs">{roleInfo?.label} Login</p>
                </div>
              </div>

              {/* Staff Login Form */}
              {selectedRole === 'staff' && (
                <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 backdrop-blur-xl">
                  <form onSubmit={handleStaffLogin} className="space-y-5" data-testid="staff-login-form">
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs font-medium">Email</Label>
                      <Input type="email" placeholder="your.email@school.edu" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
                        className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 focus:border-teal-500/50"
                        data-testid="login-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs font-medium">Password</Label>
                      <div className="relative">
                        <Input type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required
                          className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 focus:border-teal-500/50 pr-10"
                          data-testid="login-password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-11 font-semibold rounded-xl shadow-lg text-white" style={{ background: primaryColor }} disabled={loading} data-testid="login-submit-btn">
                      {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                    </Button>
                  </form>
                </div>
              )}

              {/* Parent Login / Register Form */}
              {selectedRole === 'parent' && (
                <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 backdrop-blur-xl">
                  {/* Toggle */}
                  <div className="flex bg-white/[0.06] rounded-xl p-1 mb-5">
                    <button type="button" onClick={() => setParentMode('login')}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${parentMode === 'login' ? 'bg-violet-500 text-white shadow' : 'text-white/40 hover:text-white/60'}`}>
                      Sign In
                    </button>
                    <button type="button" onClick={() => setParentMode('register')}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${parentMode === 'register' ? 'bg-violet-500 text-white shadow' : 'text-white/40 hover:text-white/60'}`}>
                      Register
                    </button>
                  </div>

                  {parentMode === 'login' ? (
                    <form onSubmit={handleParentLogin} className="space-y-4" data-testid="parent-login-form">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs font-medium">Email</Label>
                        <Input type="email" placeholder="parent@email.com" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} required autoFocus
                          className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 focus:border-violet-500/50"
                          data-testid="parent-email-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs font-medium">Password</Label>
                        <div className="relative">
                          <Input type={showParentPassword ? 'text' : 'password'} placeholder="Enter your password" value={parentPassword} onChange={(e) => setParentPassword(e.target.value)} required
                            className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 focus:border-violet-500/50 pr-10"
                            data-testid="parent-password-input" />
                          <button type="button" onClick={() => setShowParentPassword(!showParentPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                            {showParentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <Button type="submit" className="w-full h-11 font-semibold rounded-xl shadow-lg text-white bg-violet-500 hover:bg-violet-400" disabled={loading} data-testid="parent-login-submit-btn">
                        {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleParentRegister} className="space-y-3" data-testid="parent-register-form">
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs font-medium">Student Admission Number *</Label>
                        <Input placeholder="e.g. ADM-D4F2F8" value={parentAdmission} onChange={(e) => setParentAdmission(e.target.value.toUpperCase())} required
                          className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 font-mono uppercase" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-white/60 text-xs font-medium">Your Full Name *</Label>
                          <Input placeholder="Parent name" value={parentFullName} onChange={(e) => setParentFullName(e.target.value)} required
                            className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/60 text-xs font-medium">Relation</Label>
                          <select value={parentRelation} onChange={(e) => setParentRelation(e.target.value)}
                            className="h-10 w-full rounded-xl bg-white/[0.06] border border-white/[0.1] text-white text-sm px-3">
                            <option value="father" className="text-black">Father</option>
                            <option value="mother" className="text-black">Mother</option>
                            <option value="guardian" className="text-black">Guardian</option>
                          </select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/60 text-xs font-medium">Email *</Label>
                        <Input type="email" placeholder="parent@email.com" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} required
                          className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-white/60 text-xs font-medium">Phone</Label>
                          <Input placeholder="+91 98765 43210" value={parentPhone} onChange={(e) => setParentPhone(e.target.value)}
                            className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-white/60 text-xs font-medium">Password *</Label>
                          <div className="relative">
                            <Input type={showParentPassword ? 'text' : 'password'} placeholder="Min 8 chars" value={parentPassword} onChange={(e) => setParentPassword(e.target.value)} required minLength={8}
                              className="h-10 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 pr-9" />
                            <button type="button" onClick={() => setShowParentPassword(!showParentPassword)}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                              {showParentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      <Button type="submit" className="w-full h-11 font-semibold rounded-xl text-white bg-violet-500 hover:bg-violet-400 mt-1" disabled={loading}>
                        {loading ? 'Registering...' : 'Register & Link Child'} {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                      </Button>
                    </form>
                  )}
                </div>
              )}

              {/* Student Login Form */}
              {selectedRole === 'student' && (
                <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-6 backdrop-blur-xl">
                  <form onSubmit={handleStudentLogin} className="space-y-5" data-testid="student-login-form">
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs font-medium">Admission Number</Label>
                      <Input type="text" placeholder="e.g. ADM-D4F2F8" value={admissionNumber} onChange={(e) => setAdmissionNumber(e.target.value.toUpperCase())} required autoFocus
                        className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl placeholder:text-white/25 focus:border-cyan-500/50 uppercase font-mono"
                        data-testid="student-admission-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs font-medium">Date of Birth</Label>
                      <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required
                        className="h-11 bg-white/[0.06] border-white/[0.1] text-white rounded-xl focus:border-cyan-500/50"
                        data-testid="student-dob-input" />
                    </div>
                    <Button type="submit" className="w-full h-11 font-semibold rounded-xl shadow-lg text-white bg-cyan-500 hover:bg-cyan-400" disabled={loading} data-testid="student-login-submit-btn">
                      {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
